import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Search, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, CardContent, CardHeader, Input } from '@/components/ui';
import { gisApi, type GISEntitySuggestion, type GISGeometry } from '@/lib/api';
import { cn } from '@/lib/cn';

import { GISBatchGeocode } from './GISBatchGeocode';
import { GISCoordEditor } from './GISCoordEditor';
import { GISFailedCandidates } from './GISFailedCandidates';
import { GISGeocodeSearch } from './GISGeocodeSearch';
import { GISMapEditor } from './GISMapEditor';
import { GISUpload } from './GISUpload';

type EditorTab = 'pin' | 'coords' | 'geocode' | 'upload';

/**
 * Spec S07 §7.8 — GIS data input. Three-step flow:
 *   1) pick an entity (autocomplete on entity_labels)
 *   2) view its current geometry (if any)
 *   3) attach a new geometry via one of four editors:
 *      pin / coords / geocode / upload
 */
export function GISInput() {
  const { t, i18n } = useTranslation(['studio', 'common']);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<GISEntitySuggestion | null>(null);
  const [tab, setTab] = useState<EditorTab>('pin');
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  // Carries the label of a failed candidate sent into the geocode tab
  // so the search box opens pre-filled and auto-runs the lookup.
  const [pendingGeocodeQuery, setPendingGeocodeQuery] = useState<string>('');

  const geometryQuery = useQuery({
    queryKey: ['studio', 'gis', 'geometry', selected?.entity_iri],
    queryFn: () => (selected ? gisApi.getGeometry(selected.entity_iri) : Promise.resolve(null)),
    enabled: !!selected,
  });

  const save = useMutation({
    mutationFn: (payload: {
      geom_geojson: GeoJSON.Geometry;
      source: 'manual' | 'geocoded' | 'uploaded';
      source_label?: string;
    }) => {
      if (!selected) throw new Error('no_entity_selected');
      return gisApi.putGeometry(selected.entity_iri, payload);
    },
    onSuccess: () => {
      setSavedMessage(t('studio:gis.editor.saved'));
      qc.invalidateQueries({ queryKey: ['studio', 'gis', 'geometry', selected?.entity_iri] });
    },
  });

  const clear = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error('no_entity_selected');
      return gisApi.deleteGeometry(selected.entity_iri);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['studio', 'gis', 'geometry', selected?.entity_iri] });
    },
  });

  useEffect(() => {
    if (!savedMessage) return;
    const id = setTimeout(() => setSavedMessage(null), 4000);
    return () => clearTimeout(id);
  }, [savedMessage]);

  const tabs: { id: EditorTab; label: string }[] = [
    { id: 'pin', label: t('studio:gis.editor.tabs.pin') },
    { id: 'coords', label: t('studio:gis.editor.tabs.coords') },
    { id: 'geocode', label: t('studio:gis.editor.tabs.geocode') },
    { id: 'upload', label: t('studio:gis.editor.tabs.upload') },
  ];

  const onSubmit = (
    geom: GeoJSON.Geometry,
    source: 'manual' | 'geocoded' | 'uploaded',
    label?: string,
  ) => {
    if (!selected) return;
    const payload: {
      geom_geojson: GeoJSON.Geometry;
      source: 'manual' | 'geocoded' | 'uploaded';
      source_label?: string;
    } = { geom_geojson: geom, source };
    if (label) payload.source_label = label;
    save.mutate(payload);
  };

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-display text-brand-900">{t('studio:gis.title')}</h1>
        <p className="mt-1 text-body text-slate-600">{t('studio:gis.subtitle')}</p>
      </header>

      <GISBatchGeocode />

      <GISFailedCandidates
        onAddManually={(entity) => {
          setSelected(entity);
          setPendingGeocodeQuery('');
          // Default to the coords tab — that's the fastest path for an
          // analyst correcting a failed geocode.
          setTab('coords');
        }}
        onSearchAddress={(entity, query) => {
          setSelected(entity);
          setPendingGeocodeQuery(query);
          // Open the geocode tab so the analyst can refine the
          // Nominatim search instead of typing coordinates by hand.
          setTab('geocode');
        }}
      />

      <EntityPicker selected={selected} onSelect={setSelected} />

      {selected && (
        <>
          <CurrentGeometryCard
            geometry={geometryQuery.data ?? null}
            loading={geometryQuery.isLoading}
            onClear={() => clear.mutate()}
            clearing={clear.isPending}
            locale={i18n.language}
          />

          <Card>
            <CardHeader>
              <h2 className="text-h2 text-brand-900">{t('studio:gis.editor.title')}</h2>
              <div className="mt-3 flex flex-wrap gap-1">
                {tabs.map((x) => (
                  <button
                    key={x.id}
                    type="button"
                    onClick={() => {
                      setTab(x.id);
                      // Drop any pending geocode query when the analyst
                      // navigates manually so coming back to the geocode
                      // tab later doesn't re-fire a stale search.
                      if (x.id !== 'geocode') setPendingGeocodeQuery('');
                    }}
                    className={cn(
                      'rounded px-3 py-1.5 text-small transition-colors',
                      tab === x.id
                        ? 'bg-brand-50 font-medium text-brand-900'
                        : 'text-slate-600 hover:bg-slate-50',
                    )}
                  >
                    {x.label}
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {tab === 'pin' && (
                <GISMapEditor
                  initialGeometry={geometryQuery.data?.geom_geojson ?? null}
                  onSubmit={(geom) => onSubmit(geom, 'manual')}
                  pending={save.isPending}
                />
              )}
              {tab === 'coords' && (
                <GISCoordEditor
                  onSubmit={(geom) => onSubmit(geom, 'manual')}
                  pending={save.isPending}
                />
              )}
              {tab === 'geocode' && (
                <GISGeocodeSearch
                  onSubmit={(geom, label) => onSubmit(geom, 'geocoded', label)}
                  pending={save.isPending}
                  initialQuery={pendingGeocodeQuery}
                />
              )}
              {tab === 'upload' && (
                <GISUpload
                  onSubmit={(geom, label) => onSubmit(geom, 'uploaded', label)}
                  pending={save.isPending}
                />
              )}

              {savedMessage && (
                <p className="mt-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-small text-green-800">
                  {savedMessage}
                </p>
              )}
              {save.isError && (
                <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
                  {(save.error as Error).message}
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

interface EntityPickerProps {
  selected: GISEntitySuggestion | null;
  onSelect: (e: GISEntitySuggestion | null) => void;
}

function EntityPicker({ selected, onSelect }: EntityPickerProps) {
  const { t } = useTranslation('studio');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 200);
    return () => clearTimeout(id);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'gis', 'entities', debouncedQ],
    queryFn: () => gisApi.searchEntities(debouncedQ),
    enabled: debouncedQ.trim().length >= 1 && !selected,
  });

  if (selected) {
    return (
      <Card>
        <CardContent className="flex items-center justify-between gap-3 py-3">
          <div className="flex-1 min-w-0">
            <p className="text-small text-slate-500">{t('gis.search.selected')}</p>
            <p className="truncate font-medium text-brand-900">{selected.label}</p>
            <p className="truncate font-mono text-small text-slate-500">
              {selected.entity_iri}
            </p>
          </div>
          <Badge variant="neutral">{selected.entity_class}</Badge>
          <Button variant="ghost" size="icon" onClick={() => onSelect(null)} aria-label="Clear">
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <label htmlFor="gis-entity-search" className="text-h4 text-slate-700">
          {t('gis.search.label')}
        </label>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden
          />
          <Input
            id="gis-entity-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('gis.search.placeholder')}
            className="pl-8"
            autoFocus
          />
        </div>
        {debouncedQ.length >= 1 && (
          <ul className="max-h-64 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100">
            {isLoading && (
              <li className="px-3 py-2 text-small text-slate-500">…</li>
            )}
            {!isLoading && data && data.length === 0 && (
              <li className="px-3 py-2 text-small text-slate-500">
                {t('gis.search.noMatch')}
              </li>
            )}
            {data &&
              data.map((row) => (
                <li key={row.entity_iri}>
                  <button
                    type="button"
                    onClick={() => onSelect(row)}
                    className="block w-full px-3 py-2 text-left transition-colors hover:bg-slate-50"
                  >
                    <p className="font-medium text-brand-900">{row.label}</p>
                    <p className="font-mono text-small text-slate-500">
                      {row.entity_class}
                    </p>
                  </button>
                </li>
              ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

interface CurrentGeometryCardProps {
  geometry: GISGeometry | null;
  loading: boolean;
  onClear: () => void;
  clearing: boolean;
  locale: string;
}

function CurrentGeometryCard({
  geometry,
  loading,
  onClear,
  clearing,
  locale,
}: CurrentGeometryCardProps) {
  const { t } = useTranslation(['studio', 'common']);

  return (
    <Card>
      <CardHeader>
        <h2 className="text-h2 text-brand-900">{t('studio:gis.currentGeometry.title')}</h2>
      </CardHeader>
      <CardContent>
        {loading && <p className="text-small text-slate-500">{t('common:status.loading')}</p>}
        {!loading && !geometry && (
          <p className="text-body text-slate-500">{t('studio:gis.currentGeometry.none')}</p>
        )}
        {geometry && (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <MapPin className="h-4 w-4 text-accent" aria-hidden />
              <Badge variant="success">{geometry.geom_type}</Badge>
              <span className="text-small text-slate-500">
                {t('studio:gis.currentGeometry.source', { source: geometry.source })}
              </span>
              {geometry.source_label && (
                <span className="text-small text-slate-500">
                  {t('studio:gis.currentGeometry.sourceLabel', { label: geometry.source_label })}
                </span>
              )}
            </div>
            <p className="text-small text-slate-500">
              {t('studio:gis.currentGeometry.updated', {
                when: new Date(geometry.updated_at).toLocaleString(locale),
              })}
            </p>
            <pre className="rounded bg-slate-50 p-2 font-mono text-small text-slate-700 overflow-x-auto">
              {JSON.stringify(geometry.geom_geojson, null, 2)}
            </pre>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              disabled={clearing}
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" aria-hidden />
              {t('studio:gis.currentGeometry.clear')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
